<template>
    <div class="chart" :style="{ height }">
        <div class="chart-cover" @click="hideViewDetail" :class="{ open: chartCoverOpen }" :id="`chart-cover-${chart.chartId}`"></div>
        <div class="chart-loading export-png-loader" :class="{ open: chart.loading }"    :id="`chart-loading-${chart.chartId}`">
            <i class="fa fa-spinner fa-spin"></i>
        </div>
        <SettingsPopup></SettingsPopup>
        <template v-if="chart.state === 'new'">
            <NewChart :chart="chart"
                :chartTypes="chartTypes"
                :cumulativeTypes="cumulativeTypes"
                :growthRates="growthRates"
                :growthRatesQuarters="growthRatesQuarters"
                :companies="companies"
                :categories="categories"
                :subcategories="subcategories"
                :classes="classes"
                :ranges="ranges"
                :revenueFilterRanges="revenueFilterRanges"
                :brands="brands"
                :excludeBrands="excludeBrands"
                :tags="tags"
                :height="initialHeight"
                :fine-print="finePrint"
                @loading="chartLoading"
                @loaded="chartLoaded">
            </NewChart>
        </template>
        <template v-else-if="chart.state === 'load'">
            <LoadChart
                :chartTypes="chartTypes"
                :cumulativeTypes="cumulativeTypes"
                :growthRates="growthRates"
                :growthRatesQuarters="growthRatesQuarters"
                :companies="companies"
                :categories="categories"
                :subcategories="subcategories"
                :classes="classes"
                :ranges="ranges"
                :revenueFilterRanges="revenueFilterRanges"
                :brands="brands"
                :excludeBrands="excludeBrands"
                :tags="tags"
                :fine-print="finePrint">
            </LoadChart>
        </template>
        <template v-else>
            <ViewChart :chart="chart"
                :chartTypes="chartTypes"
                :cumulativeTypes="cumulativeTypes"
                :growthRates="growthRates"
                :growthRatesQuarters="growthRatesQuarters"
                :companies="companies"
                :categories="categories"
                :subcategories="subcategories"
                :classes="classes"
                :ranges="ranges"
                :revenueFilterRanges="revenueFilterRanges"
                :brands="brands"
                :excludeBrands="excludeBrands"
                :tags="tags"
                :height="initialHeight"
                :fine-print="finePrint"
                @loading="chartLoading"
                @loaded="chartLoaded">
            </ViewChart>
        </template>
    </div>
</template>

<script>

import NewChart from './NewChartComponent.vue';
import LoadChart from './LoadChartComponent.vue';
import ViewChart from './ViewChartComponent.vue';
import SettingsPopup from './SettingsPopup.vue';

import filters from '../../constants/filters';

export default {
    props: {
        chart: {
            type: Object,
        },
        initialHeight: {
            type: Number,
        },
    },
    components: {
        NewChart,
        LoadChart,
        ViewChart,
        SettingsPopup,
    },
    computed: {
        chartCoverOpen() {
            return this.chart.filters.open
                || this.chart.viewCompetitors.open
                || this.chart.viewDetails.open
                || this.chart.loading;
        },
        companies() {
            return this.$store.getters['company/getAll'].map(company => ({
                text: company.name,
                value: company.id,
            }));
        },
        categories() {
            return this.$store.getters['category/getAll'].map(category => ({
                text: category.name,
                value: category.id,
            }));
        },
        subcategories() {
            return this.$store.getters['subcategory/getAll']
                .map(subcategory => ({
                    text: subcategory.name,
                    value: subcategory.id,
                    parentId: subcategory.parent_id,
                }));
        },
        classes() {
            return this.$store.getters['class/getAll'].map(singleClass => ({
                text: singleClass.name,
                value: singleClass.id,
            }));
        },
        ranges() {
            const currentYear = parseInt(new Date().getFullYear(), 10);
            const rangesArray = [];

            for (let i = 1; i < filters.maxRange + 2; i += 1) {
                rangesArray.push({
                    text: `${currentYear - (i - 1)}`,
                    value: `${currentYear - (i - 1)}`,
                });
            }

            return rangesArray;
        },
        revenueFilterRanges() {
            const currentYear = parseInt(new Date().getFullYear(), 10);
            const revenueFilterRangesArray = [];

            for (let i = 0; i < (currentYear - filters.minYear) + 1 ; i += 1) {
                revenueFilterRangesArray.push({
                    text: `${currentYear - (i)}`,
                    value: `${currentYear - (i)}`,
                });
            }

            return revenueFilterRangesArray;
        },
        brands() {
            return this.$store.getters['brand/getAll'].map(brand => ({
                text: brand.brand_name,
                value: brand.id,
            }));
        },
        excludeBrands() {
            return this.$store.getters['brand/getAll'].map(brand => ({
                text: brand.brand_name,
                value: brand.id,
            }));
        },
        tags() {
            return this.$store.getters['tag/getAll'].map(tag => ({
                text: tag.name,
                value: tag.id,
                categories: tag.categories,
            }));
        },
        finePrint() {
            return this.$store.getters['finePrint/getAll'].map(finePrint => ({
                text: finePrint.description,
                active: finePrint.active,
                value: finePrint.id,
            }));
        },
    },
    data() {
        return {
            height: 0,
            chartTypes: filters.chartTypes,
            cumulativeTypes: filters.cumulativeTypes,
            growthRates: filters.growthRates,
            growthRatesQuarters: filters.growthRatesQuarters,
        };
    },
    created() {
        this.chartLoading();
    },
    methods: {
        hideViewDetail(){
            this.chart.viewDetails.open=false;
        },
        chartLoading() {
            this.height = `${this.initialHeight}px`;
        },
        chartLoaded() {
            this.height = '100%';
        },
    },
};

</script>

<style lang="scss">
    @import '../../style/Charts/ChartComponent.scss';
</style>
